<template>
  <section class="w-full p-8">
    <div v-if="!hideBranding" class="flex justify-center" data-testid="sign-in-modal-branding">
      <ImageSvg name="general.logoWithText" />
    </div>
    <TitleSeparator><h2>Sign in with</h2></TitleSeparator>
    <SocialAuth :redirect-url="props.redirectUrl" @action="onSocialAction" :in-progress="socialInProgress" />
    <TitleSeparator><p>Or</p></TitleSeparator>
    <Form @submit="onSubmit" :validation-schema="schema">
      <fieldset class="flex flex-col gap-4">
        <div class="relative flex flex-col gap-1">
          <label htmlFor="emailInput" class="text-sm font-bold dark:text-zinc-300">Email</label>
          <Field
            type="text"
            name="email"
            id="emailInput"
            data-testid="sign-in-email"
            class="w-full px-3 overflow-y-hidden bg-white bg-no-repeat border outline-none rounded-10 dark:bg-zinc-800 h-9 border-slate-300 dark:border-zinc-500 focus:border-blue-500 focus:ring focus:ring-blue-500/50 active:border-slate-600"
          />
          <ErrorMessage as="p" class="mt-2 text-sm text-slate-500 dark:text-zinc-400" name="email" />
        </div>
        <div class="relative flex flex-col gap-1">
          <label htmlFor="passwordInput" class="text-sm font-bold dark:text-zinc-300">Password</label>
          <Field
            type="password"
            name="password"
            id="passwordInput"
            data-testid="sign-in-password"
            class="w-full px-3 overflow-y-hidden bg-white bg-no-repeat border outline-none rounded-10 dark:bg-zinc-800 h-9 border-slate-300 dark:border-zinc-500 focus:border-blue-500 focus:ring focus:ring-blue-500/50 active:border-slate-600"
          />
          <ErrorMessage as="p" class="mt-2 text-sm text-slate-500 dark:text-zinc-400" name="password" />
          <a
            href="/forgot_password"
            data-testid="sign-in-forgot-password"
            class="absolute top-0 right-0 mt-px text-sm text-blue-500 rounded-sm hover:text-blue-400 focus-visible:ring focus-visible:ring-blue-500/50 focus-visible:bg-blue-500/10 focus:outline-none"
            >Forgot password?</a
          >
        </div>
        <Button class="!w-full" data-testid="sign-in" :loading="requestInProgress" :disabled="requestInProgress" type="solid" variant="blue" :submit="true">
          Sign in
        </Button>
      </fieldset>
    </Form>

    <p v-if="!hideBranding" class="mt-4 text-sm text-center" data-testid="sign-in-modal-signup-link">
      New to Zight?
      <a
        href="#"
        @click.stop.prevent="showSignUpModal({ redirectUrl: redirectUrl })"
        data-testid="sign-up-show-modal"
        class="text-blue-500 underline rounded-sm hover:text-blue-400 focus-visible:ring focus-visible:ring-blue-500/50 focus-visible:bg-blue-500/10 focus:outline-none"
        >Sign up for free</a
      >
    </p>
  </section>
</template>
<script setup lang="ts">
import { object, string } from 'yup';
import { Form, Field, ErrorMessage } from 'vee-validate';
import { useUserStore } from '@/store/useUserStore';
import SocialAuth from './auth_dialog/Social.vue';
import { useAuth } from '@/composables/useAuth';
import TitleSeparator from './auth_dialog/TitleSeparator.vue';
import { useToast } from '@/composables/useToast';
import { useAuthModals } from '@/composables/useAuthModals';
import { useBranding } from '@/composables/useBranding';
import { ref } from 'vue';
import { Button, ImageSvg } from '@/storybook';

const props = defineProps<{
  redirectUrl?: string;
}>();

const userStore = useUserStore();

const { showSignUpModal } = useAuthModals();
const { hideBranding } = useBranding();
const requestInProgress = ref(false);

const schema = object().shape({
  email: string().email().required(),
  password: string().min(6).required(),
});

const { onSocialAction, inProgress: socialInProgress } = useAuth(props.redirectUrl);

const { error } = useToast();
const emits = defineEmits(['close']);

const onSubmit = async (data: any) => {
  try {
    requestInProgress.value = true;
    const response = await userStore.signIn(data);

    if (response && props.redirectUrl) {
      window.location.assign(props.redirectUrl);
      return;
    }

    if (response && response.metadata && response.metadata.redirectTo) {
      window.location.assign(response.metadata.redirectTo);
      return;
    }

    await userStore.load();

    requestInProgress.value = false;
    emits('close');
  } catch (err: any) {
    requestInProgress.value = false;
    error(err.message);
  }
};
</script>
